/**
 * Request Access Page Component
 * Allows users to request access to the system
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Message, Card } from '../components/ui';
import './RequestAccess.css';

const RequestAccess = () => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    organization: '',
    jobTitle: '',
    phone: '',
    reason: '',
  });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    if (type === 'error') {
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await fetch('/api/access/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setSubmissionResult(data);
        if (data.autoApproved) {
          showMessage('Your access has been automatically approved!', 'success');
        } else {
          showMessage('Your request has been submitted successfully!', 'success');
        }
      } else {
        showMessage(data.error || 'Failed to submit request', 'error');
      }
    } catch (error) {
      showMessage('Network error: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Show success state with credentials (if auto-approved) or confirmation
  if (submissionResult) {
    return (
      <div className="rf-request-access-page">
        <div className="rf-center-container">
          <Card className="rf-access-card">
            {submissionResult.autoApproved ? (
              <>
                <div className="rf-success-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h2 className="rf-success-title">Access Approved!</h2>
                <p className="rf-success-message">
                  Your access has been automatically approved. Please save your credentials below.
                </p>

                <div className="rf-credentials-box">
                  <h3>Your Login Credentials</h3>
                  <div className="rf-credential-item">
                    <label>Username:</label>
                    <span className="rf-credential-value">{submissionResult.username}</span>
                    <button
                      className={`rf-copy-btn ${copiedField === 'username' ? 'rf-copied' : ''}`}
                      onClick={() => handleCopy(submissionResult.username, 'username')}
                      title={copiedField === 'username' ? 'Copied!' : 'Copy username'}
                    >
                      <i className={`fas ${copiedField === 'username' ? 'fa-check' : 'fa-copy'}`}></i>
                      {copiedField === 'username' && <span className="rf-copy-tooltip">Copied!</span>}
                    </button>
                  </div>
                  <div className="rf-credential-item">
                    <label>Temporary Password:</label>
                    <span className="rf-credential-value rf-password">{submissionResult.tempPassword}</span>
                    <button
                      className={`rf-copy-btn ${copiedField === 'password' ? 'rf-copied' : ''}`}
                      onClick={() => handleCopy(submissionResult.tempPassword, 'password')}
                      title={copiedField === 'password' ? 'Copied!' : 'Copy password'}
                    >
                      <i className={`fas ${copiedField === 'password' ? 'fa-check' : 'fa-copy'}`}></i>
                      {copiedField === 'password' && <span className="rf-copy-tooltip">Copied!</span>}
                    </button>
                  </div>
                  <p className="rf-credential-warning">
                    <i className="fas fa-exclamation-triangle"></i>
                    Save these credentials now. You won't be able to see the password again.
                  </p>
                </div>

                <Link to="/login" className="rf-btn rf-btn-primary rf-btn-block">
                  <i className="fas fa-sign-in-alt"></i>
                  Go to Login
                </Link>
              </>
            ) : (
              <>
                <div className="rf-success-icon rf-pending">
                  <i className="fas fa-clock"></i>
                </div>
                <h2 className="rf-success-title">Request Submitted</h2>
                <p className="rf-success-message">
                  Your access request has been submitted and is pending administrator approval.
                  You will be notified once your request has been processed.
                </p>

                <div className="rf-request-info">
                  <p><strong>Request ID:</strong> {submissionResult.requestId}</p>
                  <p className="rf-info-note">
                    Save this ID to check your request status later.
                  </p>
                </div>

                <div className="rf-action-buttons">
                  <Link to="/login" className="rf-btn rf-btn-outline">
                    <i className="fas fa-arrow-left"></i>
                    Back to Login
                  </Link>
                  <button
                    className="rf-btn rf-btn-secondary"
                    onClick={() => {
                      setSubmissionResult(null);
                      setFormData({
                        email: '',
                        firstName: '',
                        lastName: '',
                        organization: '',
                        jobTitle: '',
                        phone: '',
                        reason: '',
                      });
                    }}
                  >
                    <i className="fas fa-plus"></i>
                    Submit Another Request
                  </button>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="rf-request-access-page">
      <div className="rf-center-container">
        <Card className="rf-access-card">
          <div className="rf-auth-logo">
            <i className="fas fa-user-plus"></i>
            <span>Request Access</span>
          </div>

          <p className="rf-access-description">
            Fill out the form below to request access to the Rapid Fusion Resource Portal.
            Your request will be reviewed by an administrator.
          </p>

          <form onSubmit={handleSubmit} className="rf-access-form">
            <div className="rf-form-row">
              <Input
                label="First Name"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                placeholder="Enter your first name"
              />
              <Input
                label="Last Name"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                placeholder="Enter your last name"
              />
            </div>

            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your work email"
            />

            <div className="rf-form-row">
              <Input
                label="Organization / Company"
                type="text"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                placeholder="Enter your organization"
              />
              <Input
                label="Job Title"
                type="text"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleChange}
                placeholder="Enter your job title"
              />
            </div>

            <Input
              label="Phone Number"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter your phone number (optional)"
            />

            <div className="rf-form-group">
              <label className="rf-input-label">
                Reason for Access
              </label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Please describe why you need access to this portal..."
                className="rf-textarea"
                rows={4}
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              block
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i>
                  Submit Request
                </>
              )}
            </Button>
          </form>

          {message.text && (
            <Message type={message.type}>{message.text}</Message>
          )}

          <div className="rf-access-footer">
            <p>Already have an account?</p>
            <Link to="/login" className="rf-link">
              <i className="fas fa-sign-in-alt"></i>
              Log in here
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RequestAccess;
